/* QuickImpulseModal — "Bara snabbt"-akut.
 * When ADHD impulse hits ("I'll just quickly do that"), this guides the user:
 * 1. Capture the impulse (write it down → brain releases urgency)
 * 2. Apply 2-min rule: <2 min → do it, else PARK it
 * 3. Reconnect with what they were doing before */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, AlertOctagon, Timer, ArrowRight, Trash2, CheckCircle2, RotateCw, ListTodo, ChevronDown, ChevronUp } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { key: 'work',      emoji: '💼', label: 'Jobb',         color: '#A2D2FF' },
  { key: 'personal',  emoji: '💖', label: 'Privat',       color: '#FFC8DD' },
  { key: 'household', emoji: '🏠', label: 'Hemmet',       color: '#B9FBC0' },
  { key: 'creative',  emoji: '🎨', label: 'Kreativt',     color: '#CDB4DB' },
  { key: 'dopamine',  emoji: '🦝', label: 'Dopamin-lockning', color: '#FFE8A3' },
];

function fmtRelative(iso) {
  if (!iso) return '';
  const t = new Date(iso);
  const diff = (Date.now() - t.getTime()) / 1000;
  if (diff < 60) return 'just nu';
  if (diff < 3600) return `${Math.round(diff / 60)}m sedan`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h sedan`;
  return t.toLocaleDateString('sv-SE');
}

// ============== 2-MIN TIMER ==============
function TwoMinTimer({ onDone }) {
  const [left, setLeft] = useState(120);
  const ref = useRef(null);
  useEffect(() => {
    ref.current = setInterval(() => {
      setLeft((s) => {
        if (s <= 1) { clearInterval(ref.current); return 0; }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(ref.current);
  }, []);
  const mm = Math.floor(left / 60);
  const ss = String(left % 60).padStart(2, '0');
  const pct = ((120 - left) / 120) * 100;

  return (
    <div className="bg-gradient-to-br from-[#FFE8A3] to-[#FFC8DD] rounded-3xl p-6 text-center space-y-3" data-testid="impulse-2min-timer">
      <p className="text-[10px] font-bold uppercase tracking-[0.3em] opacity-60">2-min regel</p>
      <div className="text-5xl">⏱️</div>
      <p className="text-sm">Gå och gör det <b>nu</b> — när timern går av, tillbaka till jobbet.</p>
      <div className="relative w-32 h-32 mx-auto">
        <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
          <circle cx="50" cy="50" r="44" fill="none" stroke="rgba(0,0,0,0.1)" strokeWidth="8" />
          <circle cx="50" cy="50" r="44" fill="none" stroke="#D06892" strokeWidth="8"
            strokeDasharray={`${2 * Math.PI * 44}`}
            strokeDashoffset={`${2 * Math.PI * 44 * (1 - pct / 100)}`}
            strokeLinecap="round"
            style={{ transition: 'stroke-dashoffset 1s linear' }} />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center font-['Fredoka'] font-bold text-3xl">
          {mm}:{ss}
        </div>
      </div>
      {left === 0 && (
        <p className="text-sm font-bold text-green-700">✓ Tiden ute — nu tillbaka till det viktiga! 💖</p>
      )}
      <button onClick={onDone}
        className="neo-btn w-full py-3 bg-white font-bold text-sm"
        data-testid="impulse-2min-back">
        {left === 0 ? 'Tillbaka till jobbet 🌸' : 'Klar tidigare — tillbaka'}
      </button>
    </div>
  );
}

// ============== STEP 1: CAPTURE ==============
function CaptureStep({ onNext, previousTask }) {
  const [text, setText] = useState('');
  const [category, setCategory] = useState('dopamine');

  return (
    <div className="neo-card p-4 bg-gradient-to-br from-[#FFE8F5] via-[#FFFAF0] to-[#FFE8A3] space-y-3" data-testid="impulse-step-capture">
      <div className="text-center space-y-1">
        <div className="text-4xl">🛑</div>
        <h2 className="font-['Fredoka'] font-bold text-lg">Andas. Vad ville du "bara snabbt" göra?</h2>
        <p className="text-xs opacity-70 italic">Skriv ner det — så släpper hjärnan trycket 🐾</p>
      </div>

      {previousTask && (
        <div className="neo-card p-2 bg-white text-xs flex items-center gap-2" data-testid="impulse-prev-task">
          <span className="text-[10px] font-bold uppercase opacity-60">Du höll på med:</span>
          <span className="font-bold truncate">{previousTask}</span>
        </div>
      )}

      <textarea value={text} onChange={(e) => setText(e.target.value)}
        placeholder="t.ex. 'Måste flytta krukan', 'kolla mejlen snabbt', 'beställa det där…'"
        rows={3} autoFocus
        className="neo-input w-full text-sm" data-testid="impulse-text" />

      <div>
        <p className="text-[10px] font-bold uppercase opacity-60 mb-1">Vad är det för typ?</p>
        <div className="flex gap-1 flex-wrap">
          {CATEGORIES.map((c) => (
            <button key={c.key} onClick={() => setCategory(c.key)}
              className={`px-2 py-1 rounded-full text-[11px] font-bold border-2 transition ${category === c.key ? 'ring-2 ring-[#D06892]' : 'opacity-70'}`}
              style={{ backgroundColor: c.color, borderColor: 'rgba(0,0,0,0.1)' }}
              data-testid={`impulse-cat-${c.key}`}>
              {c.emoji} {c.label}
            </button>
          ))}
        </div>
      </div>

      <button onClick={() => onNext({ text: text.trim(), category })}
        disabled={!text.trim()}
        className="neo-btn w-full py-3 bg-[#B9FBC0] font-bold text-base flex items-center justify-center gap-1"
        data-testid="impulse-next">
        Nästa <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  );
}

// ============== STEP 2: 2-MIN RULE ==============
function RuleStep({ data, onDoNow, onPark, onBack }) {
  return (
    <div className="neo-card p-4 bg-gradient-to-br from-[#FFF8FB] to-[#E8F4FF] space-y-3" data-testid="impulse-step-rule">
      <div className="text-center space-y-1">
        <div className="text-4xl">🤔</div>
        <h2 className="font-['Fredoka'] font-bold text-lg">2-min-regeln</h2>
        <p className="text-sm">Tar <b className="text-[#D06892]">"{data.text}"</b> verkligen mindre än 2 min?</p>
        <p className="text-[10px] opacity-60 italic">Tips: Räkna i ditt huvud. Är du säker?</p>
      </div>

      <div className="space-y-2">
        <button onClick={onDoNow}
          className="neo-btn w-full py-3 bg-[#B9FBC0] font-bold text-base flex items-center justify-center gap-2"
          data-testid="impulse-do-now">
          <Timer className="w-4 h-4" /> Ja, &lt;2 min — gör det NU med timer
        </button>
        <button onClick={onPark}
          className="neo-btn w-full py-3 bg-[#FFE8F5] font-bold text-base flex items-center justify-center gap-2"
          data-testid="impulse-park">
          <ListTodo className="w-4 h-4" /> Nej / vet inte — park det till senare
        </button>
      </div>

      <button onClick={onBack}
        className="neo-btn w-full py-2 bg-white text-xs opacity-70"
        data-testid="impulse-back">
        ← Ändra texten
      </button>
    </div>
  );
}

// ============== STEP 3: PARKED ==============
function ParkedStep({ data, onClose }) {
  return (
    <div className="neo-card p-4 bg-gradient-to-br from-[#B9FBC0] to-[#E8F4FF] space-y-3 text-center" data-testid="impulse-step-parked">
      <div className="text-5xl">🐾✨</div>
      <h2 className="font-['Fredoka'] font-bold text-lg">Parkerat!</h2>
      <p className="text-sm">
        "{data.text}" är sparat i din park-lista. Hjärnan kan släppa det nu 💖
      </p>
      <p className="text-xs opacity-70 italic">
        Du kan kolla park-listan när du har dedikerad "park-tid" — t.ex. en lunchpaus.
      </p>
      {data.previous_task && (
        <div className="neo-card p-3 bg-white text-left text-sm space-y-1">
          <p className="text-[10px] font-bold uppercase opacity-60">🌸 Tillbaka till:</p>
          <p className="font-bold">{data.previous_task}</p>
        </div>
      )}
      <button onClick={onClose}
        className="neo-btn w-full py-3 bg-[#FFC8DD] font-bold text-base"
        data-testid="impulse-close-parked">
        Tillbaka till jobbet 🚀
      </button>
    </div>
  );
}

// ============== PARK LIST ==============
function ParkListItem({ item, onDone, onDefer, onDelete }) {
  const cat = CATEGORIES.find((c) => c.key === item.category) || CATEGORIES[CATEGORIES.length - 1];
  return (
    <li className="neo-card p-2 bg-white text-xs flex items-start gap-2" data-testid={`park-item-${item.id}`}>
      <span className="text-lg shrink-0">{cat.emoji}</span>
      <div className="flex-1 min-w-0">
        <p className="font-bold truncate">{item.text}</p>
        <p className="text-[9px] opacity-60">{fmtRelative(item.created_at)}{item.deferred_count > 0 ? ` · skjutit upp ${item.deferred_count}x` : ''}</p>
      </div>
      <div className="flex gap-1 shrink-0">
        <button onClick={() => onDone(item)} className="p-1 hover:bg-green-50 rounded" title="Klar" data-testid={`park-done-${item.id}`}>
          <CheckCircle2 className="w-4 h-4 text-green-600" />
        </button>
        <button onClick={() => onDefer(item)} className="p-1 hover:bg-yellow-50 rounded" title="Skjut upp" data-testid={`park-defer-${item.id}`}>
          <RotateCw className="w-4 h-4 text-yellow-600" />
        </button>
        <button onClick={() => onDelete(item)} className="p-1 hover:bg-red-50 rounded" title="Ta bort" data-testid={`park-delete-${item.id}`}>
          <Trash2 className="w-4 h-4 text-red-500" />
        </button>
      </div>
    </li>
  );
}

// ============== MAIN MODAL ==============
export default function QuickImpulseModal({ open, onClose }) {
  const [step, setStep] = useState('capture'); // capture | rule | timer | parked
  const [data, setData] = useState({ text: '', category: 'dopamine' });
  const [previousTask, setPreviousTask] = useState('');
  const [parked, setParked] = useState([]);
  const [showList, setShowList] = useState(false);

  // Reset on open + fetch most recent task for context
  useEffect(() => {
    if (!open) return;
    setStep('capture');
    setData({ text: '', category: 'dopamine' });

    // Fetch recent task (best-effort) for "what were you doing"
    axios.get(`${API}/tasks`, { withCredentials: true })
      .then((r) => {
        const tasks = Array.isArray(r.data) ? r.data : [];
        const active = tasks.find((t) => !t.completed);
        if (active) setPreviousTask(active.title || '');
      })
      .catch(() => {});

    // Fetch park list
    refreshList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const refreshList = useCallback(async () => {
    try {
      const { data: d } = await axios.get(`${API}/distractions`, { withCredentials: true });
      setParked(d?.pending || []);
    } catch { /* silent */ }
  }, []);

  const next = (payload) => {
    setData({ ...data, ...payload });
    setStep('rule');
  };

  const doNow = async () => {
    // Park as "now2min" + start timer immediately
    try {
      await axios.post(`${API}/distractions`, {
        text: data.text,
        category: data.category,
        urgency: 'now2min',
        previous_task: previousTask || null,
      }, { withCredentials: true });
    } catch { /* */ }
    setStep('timer');
  };

  const park = async () => {
    try {
      await axios.post(`${API}/distractions`, {
        text: data.text,
        category: data.category,
        urgency: 'later',
        previous_task: previousTask || null,
      }, { withCredentials: true });
      setData((d) => ({ ...d, previous_task: previousTask || null }));
      toast.success('Parkerat 🐾 — fortsätt med det viktiga');
      setStep('parked');
      refreshList();
    } catch {
      toast.error('Kunde inte parkera');
    }
  };

  const doneItem = async (item) => {
    try {
      await axios.post(`${API}/distractions/${item.id}/done`, {}, { withCredentials: true });
      toast.success('Klart! +2 poäng för självdisciplin 💖');
      refreshList();
    } catch { /* */ }
  };

  const deferItem = async (item) => {
    try {
      await axios.post(`${API}/distractions/${item.id}/defer`, {}, { withCredentials: true });
      refreshList();
    } catch { /* */ }
  };

  const deleteItem = async (item) => {
    if (!window.confirm('Ta bort denna parkerade impuls?')) return;
    try {
      await axios.delete(`${API}/distractions/${item.id}`, { withCredentials: true });
      refreshList();
    } catch { /* */ }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[115] bg-black/50 backdrop-blur-sm flex items-start sm:items-center justify-center p-2 sm:p-4 overflow-y-auto"
         onClick={onClose} data-testid="impulse-modal">
      <div onClick={(e) => e.stopPropagation()}
           className="w-full max-w-md my-4 space-y-2">
        <div className="flex items-center justify-between text-white px-2">
          <div className="flex items-center gap-2">
            <AlertOctagon className="w-5 h-5 text-red-300" />
            <h3 className="font-['Fredoka'] font-bold text-base">"Bara snabbt"-akut</h3>
          </div>
          <button onClick={onClose} className="bg-white/15 hover:bg-white/25 rounded-full p-2" data-testid="impulse-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {step === 'capture' && <CaptureStep onNext={next} previousTask={previousTask} />}
        {step === 'rule' && <RuleStep data={data} onDoNow={doNow} onPark={park} onBack={() => setStep('capture')} />}
        {step === 'timer' && <TwoMinTimer onDone={onClose} />}
        {step === 'parked' && <ParkedStep data={{ ...data, previous_task: previousTask }} onClose={onClose} />}

        {/* Park list — collapsible */}
        {parked.length > 0 && step !== 'timer' && (
          <div className="neo-card bg-white p-3 mt-2" data-testid="park-list-section">
            <button onClick={() => setShowList(!showList)}
              className="w-full flex items-center justify-between text-sm font-bold"
              data-testid="park-list-toggle">
              <span className="flex items-center gap-1">
                <ListTodo className="w-4 h-4" /> Mina parkerade impulser ({parked.length})
              </span>
              {showList ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            {showList && (
              <ul className="space-y-1 mt-2 max-h-64 overflow-y-auto" data-testid="park-list">
                {parked.map((item) => (
                  <ParkListItem key={item.id} item={item}
                    onDone={doneItem} onDefer={deferItem} onDelete={deleteItem} />
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
